import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {
  CompetitionService,
  eCaseAnimations,
  ECaseConfirmDialogComponent,
  ECaseDataTable,
  ECaseDateFormatterPipe,
  ECaseSnackBarService,
  ECaseUtils,
  ECaseUtilsGlobal,
  EcaseHttpService,
  ParametersService,
  PendingChangesGuard
} from 'synto-common';
import {TranslateService} from '@ngx-translate/core';
import {MatTableDataSource} from '@angular/material/table';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {forkJoin} from 'rxjs';
import {Subscription} from 'rxjs/internal/Subscription';
import {Platform} from '@angular/cdk/platform';
import cloneDeep from 'lodash/cloneDeep';
import {MatDialog} from '@angular/material/dialog';
import {ManageEcaseApprovalCycleDialogComponent} from './ecase-manage-approval-cycle-dialog/ecase-manage-approval-cycle-dialog.component';
import {SelectEcaseApprovalCycleDialogComponent} from './ecase-select-approval-cycle-dialog/ecase-select-approval-cycle-dialog.component';

@Component({
  selector: 'ecase-approval-levels',
  templateUrl: './ecase-approval-levels.component.html',
  styleUrls: ['./ecase-approval-levels.component.scss'],
  animations: eCaseAnimations
})
export class EcaseApprovalLevelsComponent extends ECaseDataTable implements OnInit, OnDestroy {

  @Output() addApproversFormErrorStateUpdate = new EventEmitter<boolean>();
  addApproversForm: FormGroup;
  isMobile: boolean;
  displayedColumns = ['approvalCycleDescriptionId', 'approverName', 'approvalLevel', 'isNotified', 'trash', 'action'];
  filterObject = {
    approverName: '',
    approvalLevel: '',
  };
  filterObjectForSelectFields = {
    approvalCycleDescriptionId: ''
  };
  sortActive = 'approverName';
  sortDirection = 'asc';
  subscriptions: Subscription[] = [];
  filteredApprovers = [];
  filteredApprovalCycles = [];
  availableApprovalCycles: any[] = [];
  availableApprovers: any[] = [];
  @Output() dataChanged = new EventEmitter<any>();

  @Input() cpmCompetition?: number;
  @Input() cycleTypeId?: number;
  @Input() approvalCycleTypeId = 'all';
  @Input() isCompetitionClosed: boolean;
  private _standaloneIsCompetitionClosed = false;

  get effectiveIsCompetitionClosed(): boolean {
    if (this.isStandalonePaApprovals()) {
      return this._standaloneIsCompetitionClosed;
    }
    return !!this.isCompetitionClosed;
  }

  isStandalonePaApprovals(): boolean {
    return this.cycleTypeId === ECaseUtilsGlobal.APPROVAL_CYCLE_PA_REPORT_TYPE_ID
      && this.cpmCompetition != null;
  }

  private notifyParentApproversFormError(invalid: boolean): void {
    if (!this.isStandalonePaApprovals()) {
      this.addApproversFormErrorStateUpdate.emit(invalid);
    }
  }

  nextLevel: number;
  isGroupingDone = false;
  unGroupedApprovers: any[];

  constructor(
    translate: TranslateService,
    parameterService: ParametersService,
    private pendingChangesGuard: PendingChangesGuard,
    private formBuilder: FormBuilder,
    public platform: Platform,
    private dialog: MatDialog,
    public competitionService: CompetitionService,
    private eCaseHttpService: EcaseHttpService,
    private eCaseDateFormatterPipe: ECaseDateFormatterPipe,
    private eCaseSnackBarService: ECaseSnackBarService) {
    super(translate, parameterService);
  }

  _approvers: any[] = [];

  get approvers(): any[] {
    return this._approvers;
  }

  getGroupedApproversForSave(): any[] {
    return cloneDeep(this._approvers || []);
  }

  reloadApproversAndCycles(): void {
    this.loadApproversThenCycles();
  }

  private applyApproversFromApi(rawApprovers: any[], rawAvailable: any[]): void {
    const sortedAvailable = (rawAvailable || []).slice().sort((a, b) =>
      (a.name || '').localeCompare(b.name || ''));
    this.availableApprovers = sortedAvailable;
    this.isGroupingDone = false;
    this.unGroupedApprovers = cloneDeep(rawApprovers || []);
    this._approvers = this.groupApproversByCycleDescription(this.unGroupedApprovers);
    this.isGroupingDone = true;
  }

  private loadApproversThenCycles(): void {
    const cid = this.resolvedCpmCompetitionId();
    const ct = this.cycleTypeId;

    if (ct === ECaseUtilsGlobal.APPROVAL_CYCLE_PA_REPORT_TYPE_ID) {
      forkJoin({
        cyclesResponse: this.competitionService.getApprovalCyclesByCompetitionIdAndTypeId(cid, ct),
        poolResponse: this.competitionService.getApproversAvailable()
      }).subscribe({
        next: ({cyclesResponse, poolResponse}) => {
          if (!cyclesResponse?.status) {
            this.applyApproversFromApi([], []);
            this.eCaseSnackBarService.show('failure');
            return;
          }
          const rawCycles = this.toApprovalCycleArray(cyclesResponse.approvalCycles);
          this.availableApprovalCycles = rawCycles.map((c) => this.normalizeCycleForUi(cloneDeep(c)));
          const firstCycle = rawCycles[0];
          this._standaloneIsCompetitionClosed = firstCycle ? !!firstCycle.isCompetitionClosed : false;
          const flatApprovers = this.flattenPaLevelsFromTypedCycles(this.availableApprovalCycles);
          const pool = poolResponse?.status ? (poolResponse.approvers || []) : [];
          this.applyApproversFromApi(flatApprovers, pool);
          this.reBuildForm();
          this.sortColumn({active: this.sortActive, direction: this.sortDirection});
          this.filteredApprovers = [];
          this.filteredApprovalCycles = [];
        },
        error: () => {
          this.applyApproversFromApi([], []);
          this.eCaseSnackBarService.show('failure');
        }
      });
      return;
    }

    if (ct === ECaseUtilsGlobal.APPROVAL_CYCLE_AWF_TYPE_ID) {
      this.competitionService.getAwardFinalizationInformation(String(cid)).subscribe((response: any) => {
        if (response.status) {
          this.applyApproversFromApi(response.approvers || [], response.availableApprovers || []);
          this.loadApprovalCycles();
        }
      });
      return;
    }

    if (ct === ECaseUtilsGlobal.APPROVAL_CYCLE_FINANCIAL_TYPE_ID) {
      this.competitionService.getAllFinanceInfo(cid).subscribe((result: any) => {
        if (result.status && result.data) {
          const d = result.data;
          this.applyApproversFromApi(d.approvers || [], d.availableApprovers || []);
          this.loadApprovalCycles();
        }
      });
      return;
    }

    this.applyApproversFromApi([], []);
    this.loadApprovalCycles();
  }

  groupApproversByCycleDescription(approvers: any[]): any[] {
    const result = [];
    ECaseUtils.groupBy(approvers, item => item.approvalCycleDescriptionId).forEach((approversGrouped: any[]) => {
      const parentRow = cloneDeep(approversGrouped[0]);
      parentRow.isParent = true;
      parentRow.isGrouped = true;
      parentRow.isShowChildren = true;
      result.push(parentRow);
      result.push(...cloneDeep(approversGrouped).map(e => {
        e.isChild = true;
        e.isGrouped = true;
        e.isChildVisible = true;
        e.oldIndex = approvers.indexOf(approvers.filter(_e => _e.approvalCycleDescriptionId === e.approvalCycleDescriptionId
          && _e.approvalLevel === e.approvalLevel
          && _e.ssoUserId === e.ssoUserId)[0]);
        return e;
      }).sort((a, b) => {
        if (a.approvalLevel === '' || b.approvalLevel === '') {
          return 1;
        }
        if (a.approvalLevel < b.approvalLevel) {
          return -1;
        } else if (a.approvalLevel > b.approvalLevel) {
          return 1;
        }
        return 0;
      }));
    });
    return result;
  }

  toggleChildren(row): void {
    row.isShowChildren = !row.isShowChildren;
    this._approvers = this.approvers.map(e => {
      if (e.approvalCycleDescriptionId === row.approvalCycleDescriptionId) {
        e.isChildVisible = !e.isChildVisible;
      }
      return e;
    });
    this.serviceArray = this._approvers;
    this.originalArray = this._approvers;
    this.renewDataSource(this._approvers);
  }

  sortColumn(event: any, childPath?: string): void {
    this.sortActive = event.active;
    this.sortDirection = event.direction;
    this.renewDataSource(this.dataSource.data);
  }

  renewDataSource(newDataArray): void {
    this.dataSource = new MatTableDataSource(newDataArray);
    this.sortingDataAccessorWithIgnoreCase();
    this.dataSource.paginator = this.paginator.toArray()[0];
    this.dataSource.sort = this.sort.toArray()[0];
  }

  addApprover(): void {
    const dialogRef = this.dialog.open(SelectEcaseApprovalCycleDialogComponent, {
      width: '50vw'
    });
    dialogRef.componentInstance.availableApprovalCycles = this.availableApprovalCycles;
    dialogRef.afterClosed().subscribe((approvalCycleDescription) => {
      if (approvalCycleDescription) {
        const newRow = {
          approver: '',
          approvalLevel: '',
          approverName: '',
          approvalCycleDescriptionId: approvalCycleDescription.id,
          approvalCycleDescription: approvalCycleDescription.description,
          ssoUserId: ((this.approvers.length + 1) * -1),
          isNotified: true
        };
        if (!this.unGroupedApprovers) {
          this.unGroupedApprovers = [];
        }
        if (!this.unGroupedApprovers.some(e => e.approvalCycleDescriptionId === approvalCycleDescription.id)) {
          newRow.approvalLevel = '1';
        }
        this.unGroupedApprovers.push(newRow);
        this._approvers = this.groupApproversByCycleDescription(cloneDeep(this.unGroupedApprovers));
        this.reBuildForm();
        this.notifyParentApproversFormError(true);
        this.updateNextLevel({
          approvalCycleDescriptionId: approvalCycleDescription.id,
          ssoUserId: ((this.approvers.length + 1) * -1),
        });
        this.pendingChangesGuard.isPristine = false;
      }
    });
  }

  ngOnInit(): void {
    this.isMobile = this.platform.ANDROID || (this.platform.IOS && !navigator.userAgent.match(/(iPad)/));
    this.loadApproversThenCycles();
  }

  loadApprovalCycles(): void {
    const competitionId = this.resolvedCpmCompetitionId();
    const request$ = this.cycleTypeId != null
      ? this.eCaseHttpService.get(
        '/api/getApprovalCyclesByCompetitionIdAndTypeId/' + competitionId + '/' + this.cycleTypeId)
      : this.competitionService.getApprovalCyclesByCompetitionId(competitionId, this.approvalCycleTypeId);

    request$.subscribe((response: any) => {
      if (response.status) {
        this.availableApprovalCycles = this.toApprovalCycleArray(response.approvalCycles)
          .map((cycle) => this.normalizeCycleForUi(cycle));
        this.reBuildForm();
        this.sortColumn({active: this.sortActive, direction: this.sortDirection});
        this.filteredApprovers = [];
        this.filteredApprovalCycles = [];
      }
    });
  }

  private resolvedCpmCompetitionId(): number {
    return this.cpmCompetition != null ? Number(this.cpmCompetition) : Number(this.competitionService.competitionId);
  }

  private normalizeCycleForUi(cycle: any): any {
    cycle.formattedLastUpdateDate = this.eCaseDateFormatterPipe.transform(cycle.lastUpdateDate, this.syntoDate);
    const labels = cycle.cycleTypeLabel;
    const customDesc = cycle.description != null ? String(cycle.description).trim() : '';
    if (labels && typeof labels === 'object') {
      const lang = this.translate.currentLang || this.translate.getDefaultLang();
      const text = labels[lang] ?? labels.en ?? labels.fr;
      if (text && customDesc === '') {
        cycle.description = text;
      }
    }
    return cycle;
  }

  private toApprovalCycleArray(approvalCycles: any): any[] {
    if (approvalCycles == null) {
      return [];
    }
    return Array.isArray(approvalCycles) ? approvalCycles : Object.values(approvalCycles);
  }

  private toApprovalLevelArray(approvalLevels: any): any[] {
    if (approvalLevels == null) {
      return [];
    }
    return Array.isArray(approvalLevels) ? approvalLevels : Object.values(approvalLevels);
  }

  private flattenPaLevelsFromTypedCycles(cycles: any[]): any[] {
    const rows: any[] = [];
    for (const cycle of cycles) {
      const cycleId = Number(cycle.id);
      const desc = cycle.description ?? '';
      for (const lvl of this.toApprovalLevelArray(cycle.approvalLevels)) {
        const d = lvl.approverDetails || {};
        const uid = Number(d.id);
        const levelPk = lvl.levelId != null ? Number(lvl.levelId) : undefined;
        rows.push({
          approvalCycleDescriptionId: cycleId,
          approvalCycleDescription: desc,
          ssoUserId: uid,
          approver: uid > 0 ? uid : undefined,
          approverName: d.name || '',
          approvalLevel: lvl.approvalLevel != null && lvl.approvalLevel !== '' ? String(lvl.approvalLevel) : '',
          isNotified: !!lvl.isNotified,
          cpmCompetitionApprovalLevelId: levelPk != null && !Number.isNaN(levelPk) ? levelPk : undefined
        });
      }
    }
    return rows;
  }

  updateNextLevel(row): void {
    if (row) {
      const arr2 = ECaseUtils.removeDups(this.approvers
        .filter(e => e.approvalCycleDescriptionId === row.approvalCycleDescriptionId)
        .filter(e => e.ssoUserId !== row.ssoUserId)
        .map(e => e.approvalLevel)
        .filter(e => !isNaN(e) && e !== '')
        .map(e => Number(e))
        .sort());

      let skipped = 0;
      if (arr2.length > 1) {
        skipped = arr2.reduce((p, c, i, a) => c === i + a[0] ? p : i + a[0]++, void 0);
      }

      if (skipped) {
        this.nextLevel = skipped;
      } else {
        const arr = ECaseUtils.removeDups(this.approvers
          .filter(e => e.ssoUserId !== row.ssoUserId)
          .filter(e => e.approvalCycleDescriptionId === row.approvalCycleDescriptionId)
          .map(e => e.approvalLevel))
          .filter(e => !isNaN(e) && e !== '')
          .map(e => Number(e));

        if (arr.length === 0) {
          this.nextLevel = 1;
        } else {
          this.nextLevel = Math.max(...arr) + 1;
        }

      }
    } else {
      this.nextLevel = 1;
    }

  }

  removeApprover(row): void {
    this.unGroupedApprovers = this.unGroupedApprovers
      .filter(e => (e.approvalCycleDescriptionId !== row.approvalCycleDescriptionId) || (e.approvalCycleDescriptionId === row.approvalCycleDescriptionId && e.ssoUserId !== row.ssoUserId));
    this._approvers = cloneDeep(this.groupApproversByCycleDescription(this.unGroupedApprovers));
    this.reBuildForm();
    this.notifyParentApproversFormError(this.addApproversForm.invalid);
    this.updateNextLevel(row);
    const skipped = this.approvers
      .filter(e => e.approvalCycleDescriptionId === row.approvalCycleDescriptionId)
      .map(e => e.approvalLevel)
      .filter(e => !isNaN(e) && e !== '').sort().reduce((p, c, i, a) => c === i + a[0] ? p : i + a[0]++, void 0);


    if (skipped) {
      const approvalLevelToBeFiltered = Math.max(...this.approvers
        .filter(e => e.isChild).filter(e => e.approvalCycleDescriptionId === row.approvalCycleDescriptionId).map(e => e.approvalLevel));

      const index = this.approvers.indexOf(this.approvers
        .filter(e => e.isChild)
        .filter(e => e.approvalCycleDescriptionId === row.approvalCycleDescriptionId)
        .filter(e => e.approvalLevel === approvalLevelToBeFiltered)[0]);
      setTimeout(() => {
        this.addApproversForm.get('approvalLevel' + (index + 1)).markAsTouched();
        this.addApproversForm.get('approvalLevel' + (index + 1)).setErrors({invalidLevel: true});
        this.addApproversForm.get('approvalLevel' + (index + 1)).updateValueAndValidity();
        this.notifyParentApproversFormError(this.addApproversForm.invalid);
        this.pendingChangesGuard.isPristine = false;
      }, 400);
    }
  }

  private readonly confirmDeleteCycleEn =
    'Are you sure you want to remove all approvers from this approval cycle? The cycle will remain. This cannot be undone.';
  private readonly confirmDeleteApproverEn =
    'Are you sure you want to delete this approver?';

  openConfirmDeleteApprovalCycle(row: any): void {
    if (this.effectiveIsCompetitionClosed || !row?.isParent) {
      return;
    }
    const cycleId = Number(row.approvalCycleDescriptionId);
    if (!cycleId || Number.isNaN(cycleId)) {
      return;
    }
    const ref = this.dialog.open(ECaseConfirmDialogComponent, {width: '520px'});
    ref.componentInstance.confirmMessage = this.confirmDeleteCycleEn;
    ref.afterClosed().subscribe((ok) => {
      if (!ok) {
        return;
      }
      this.competitionService.deleteApprovalCycleLevels(cycleId).subscribe((res: any) => {
        if (res?.status) {
          this.eCaseSnackBarService.show('success');
          this.pendingChangesGuard.isPristine = true;
          this.loadApproversThenCycles();
        } else {
          this.eCaseSnackBarService.show('failure');
        }
      });
    });
  }

  openConfirmDeleteApprover(row: any): void {
    if (this.effectiveIsCompetitionClosed || !row?.isChild) {
      return;
    }
    const ref = this.dialog.open(ECaseConfirmDialogComponent, {width: '480px'});
    ref.componentInstance.confirmMessage = this.confirmDeleteApproverEn;
    ref.afterClosed().subscribe((ok) => {
      if (!ok) {
        return;
      }
      const levelPk = row.cpmCompetitionApprovalLevelId;
      if (levelPk != null && levelPk !== '' && !Number.isNaN(Number(levelPk)) && Number(levelPk) > 0) {
        this.competitionService.deleteApprovalLevel(Number(levelPk)).subscribe((res: any) => {
          if (res?.status) {
            this.eCaseSnackBarService.show('success');
            this.pendingChangesGuard.isPristine = true;
            this.loadApproversThenCycles();
          } else {
            this.eCaseSnackBarService.show('failure');
          }
        });
      } else {
        this.removeApprover(row);
        this.pendingChangesGuard.isPristine = false;
      }
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => {
      if (subscription) {
        subscription.unsubscribe();
      }
    });
  }

  manageCycle(): void {
    const dialogRef = this.dialog.open(ManageEcaseApprovalCycleDialogComponent, {
      width: '50vw'
    });
    dialogRef.componentInstance.approvalCycles = this.availableApprovalCycles;
    dialogRef.componentInstance.isCompetitionClosed = this.effectiveIsCompetitionClosed;
    dialogRef.componentInstance.cpmCompetition = this.resolvedCpmCompetitionId();
    if (this.cycleTypeId != null) {
      dialogRef.componentInstance.approvalCycleTypeId = this.cycleTypeId;
    }
    dialogRef.afterClosed().subscribe((decision) => {
      if (decision) {
        if (this.isStandalonePaApprovals()) {
          this.loadApproversThenCycles();
        } else {
          this.loadApprovalCycles();
          this.dataChanged.emit();
        }
      }
    });
  }

  saveStandalonePaApprovers(): void {
    if (!this.isStandalonePaApprovals() || this.effectiveIsCompetitionClosed) {
      return;
    }
    if (this.addApproversForm?.invalid) {
      this.addApproversForm.markAllAsTouched();
      this.eCaseSnackBarService.show('failure', ECaseUtils.getTranslatedValueFromKey(this.translate,
        'ecase.common.thisfieldismandatory'));
      return;
    }
    const cid = this.resolvedCpmCompetitionId();
    const typeId = this.cycleTypeId;
    if (typeId == null) {
      this.eCaseSnackBarService.show('failure');
      return;
    }
    const payload = this.buildPaFullApprovalCycleSavePayload(cid, typeId);
    this.competitionService.addOrUpdateFullApprovalCycle(payload).subscribe((res: any) => {
      if (res.status) {
        this.pendingChangesGuard.isPristine = true;
        this.eCaseSnackBarService.show('success');
        this.loadApproversThenCycles();
      } else {
        this.eCaseSnackBarService.show('failure');
      }
    });
  }

  private buildPaFullApprovalCycleSavePayload(cpmCompetitionId: number, approvalCycleTypeId: number): any[] {
    const byCycle = new Map<number, any[]>();
    for (const row of this.unGroupedApprovers || []) {
      const cycleId = Number(row.approvalCycleDescriptionId);
      if (Number.isNaN(cycleId)) {
        continue;
      }
      if (!byCycle.has(cycleId)) {
        byCycle.set(cycleId, []);
      }
      byCycle.get(cycleId).push(row);
    }
    return (this.availableApprovalCycles || []).map((c) => {
      const cycleId = Number(c.id);
      const rows = (byCycle.get(cycleId) || []).filter((e) => Number(e.ssoUserId) > 0);
      const levels = rows.map((e) => {
        const sso = Number(e.approver != null ? e.approver : e.ssoUserId);
        const level: any = {
          isNotify: !!e.isNotified,
          ssoUserId: sso,
          approvalLevel: Number(e.approvalLevel),
        };
        const lid = e.cpmCompetitionApprovalLevelId;
        if (lid != null && lid !== '' && !Number.isNaN(Number(lid)) && Number(lid) > 0) {
          level.levelId = Number(lid);
        }
        return level;
      });
      return {
        id: cycleId,
        competitionId: cpmCompetitionId,
        description: c.description != null ? String(c.description) : '',
        approvalCycleTypeId,
        levels,
      };
    });
  }

  cancelStandalonePaApprovers(): void {
    if (!this.isStandalonePaApprovals()) {
      return;
    }
    this.pendingChangesGuard.isPristine = true;
    this.loadApproversThenCycles();
  }

  displayFn(user): string | undefined {
    if (!this.availableApprovers) {
      return undefined;
    }
    return this.availableApprovers.filter(item => item.ssoUserId === user).map(item => item.name)[0];
  }

  displayFn2(cycle): string | undefined {
    if (!this.availableApprovalCycles) {
      return undefined;
    }
    return this.availableApprovalCycles.filter(item => item.id === cycle).map(item => item.description)[0];
  }

  replaceLevel(errorString: string): string {
    return errorString.replace('${nextLevel}', this.nextLevel.toString());
  }

  updateSsoUserId(event, index, oldIndex): void {
    if (!this.availableApprovers) {
      return;
    }
    this.approvers[index].ssoUserId = event.option.value;
    this.approvers[index].approverName = this.availableApprovers.filter(e => e.ssoUserId === event.option.value)[0].name;
    this.unGroupedApprovers[oldIndex].ssoUserId = event.option.value;
    this.unGroupedApprovers[oldIndex].approver = event.option.value;
    this.unGroupedApprovers[oldIndex].approverName = this.availableApprovers.filter(e => e.ssoUserId === event.option.value)[0].name;
    this.pendingChangesGuard.isPristine = false;
  }

  updateApprovalCycleId(event, index, oldApprovalCycleDescriptionId): void {
    if (!this.availableApprovalCycles) {
      return;
    }
    this._approvers = this.approvers.map((approver, _index) => {
      if (index === _index || approver.approvalCycleDescriptionId === oldApprovalCycleDescriptionId) {
        approver.approvalCycleDescriptionId = event.option.value;
        approver.approvalCycleDescription = this.availableApprovalCycles.filter(e => e.id === event.option.value)[0].description;
      }
      return approver;
    });
    this.unGroupedApprovers = this.unGroupedApprovers.map((approver, _index) => {
      if (approver.approvalCycleDescriptionId === oldApprovalCycleDescriptionId) {
        approver.approvalCycleDescriptionId = event.option.value;
        approver.approvalCycleDescription = this.availableApprovalCycles.filter(e => e.id === event.option.value)[0].description;
      }
      return approver;
    });
    this.pendingChangesGuard.isPristine = false;
  }

  reBuildForm(): void {
    if (!this.approvers) {
      return;
    }
    const cycles = this.availableApprovalCycles || [];
    const obj = {};
    this.filteredApprovers = [];
    this.approvers.filter(e => e.isParent).forEach((user) => {
      const index = this.approvers.indexOf(user);
      obj['approvalCycleDescriptionId' + (index + 1)] = [user.approvalCycleDescriptionId, [Validators.required]];
      this.filteredApprovalCycles.push(cloneDeep(cycles));
      this.filteredApprovers.push(cloneDeep([]));
    });
    this.approvers.filter(e => e.isChild).forEach((user) => {
      const index = this.approvers.indexOf(user);
      obj['approver' + (index + 1)] = [user.approver, [Validators.required]];
      obj['approvalLevel' + (index + 1)] = [user.approvalLevel, [Validators.required, Validators.max(5), Validators.min(1)]];
      obj['isNotified' + (index + 1)] = [user.isNotified];
      this.filteredApprovers.push(cloneDeep([]));
    });
    this.addApproversForm = this.formBuilder.group(obj);
    this.ngOnDestroy();
    this.subscriptions = [];
    this.approvers.filter(e => e.isParent).forEach((user) => {
      const index = this.approvers.indexOf(user);
      this.subscriptions.push(this.addApproversForm.get('approvalCycleDescriptionId' + (index + 1)).valueChanges
        .subscribe(newValue => {
          this.pendingChangesGuard.isPristine = false;
          if (!isNaN(newValue)) {
            this.approvers[index].approvalCycleDescriptionId = newValue;
          }
          this.filteredApprovalCycles[index] = this._filterApprovalCycles(newValue, this.approvers[index]);
          setTimeout(() => {
            this.notifyParentApproversFormError(this.addApproversForm.invalid);
            this.pendingChangesGuard.isPristine = false;
          }, 400);
        }));
    });
    this.approvers.filter(e => e.isChild).forEach((user) => {
      const index = this.approvers.indexOf(user);
      this.subscriptions.push(this.addApproversForm.get('approver' + (index + 1)).valueChanges
        .subscribe(newValue => {
          this.pendingChangesGuard.isPristine = false;
          this.approvers[index].approver = newValue;
          this.unGroupedApprovers[this.approvers[index].oldIndex].approver = newValue;
          this.filteredApprovers[index] = cloneDeep(this._filterApprovers(newValue));
          setTimeout(() => {
            this.notifyParentApproversFormError(this.addApproversForm.invalid);
            this.pendingChangesGuard.isPristine = false;
          }, 400);
        }));
      this.subscriptions.push(this.addApproversForm.get('isNotified' + (index + 1)).valueChanges
        .subscribe(newValue => {
          this.pendingChangesGuard.isPristine = false;
          this.approvers[index].isNotified = newValue;
          this.unGroupedApprovers[this.approvers[index].oldIndex].isNotified = newValue;
          this.pendingChangesGuard.isPristine = false;
        }));
      this.subscriptions.push(this.addApproversForm.get('approvalLevel' + (index + 1)).valueChanges.subscribe((newValue) => {
        this.approvers[index].approvalLevel = newValue;
        this.unGroupedApprovers[this.approvers[index].oldIndex].approvalLevel = newValue;

        this.pendingChangesGuard.isPristine = false;
        this.updateNextLevel(this.approvers[index]);
        if (this.approvers.filter(e => e.approvalCycleDescriptionId === this.approvers[index].approvalCycleDescriptionId).filter(e => e.approvalLevel === newValue).length > 1) {
          this.addApproversForm.get('approvalLevel' + (index + 1)).markAsTouched();
          this.addApproversForm.get('approvalLevel' + (index + 1)).setErrors({duplicateApprovalLevel: true});
        } else {
          this.addApproversForm.get('approvalLevel' + (index + 1)).setErrors(null);
        }
        if (!this.approvers.filter(e => e.approvalCycleDescriptionId === this.approvers[index].approvalCycleDescriptionId).some(e => e.approvalLevel === this.nextLevel)) {
          this.addApproversForm.get('approvalLevel' + (index + 1)).markAsTouched();
          this.addApproversForm.get('approvalLevel' + (index + 1)).setErrors({levelSkipped: true});
        } else {
          if (this.addApproversForm.get('approvalLevel' + (index + 1)).errors && !this.addApproversForm.get('approvalLevel' + (index + 1)).errors.duplicateApprovalLevel) {
            this.addApproversForm.get('approvalLevel' + (index + 1)).setErrors(null);
          }
        }

        const arr = ECaseUtils.removeDups(this.approvers
          .filter(e => e.approvalCycleDescriptionId === this.approvers[index].approvalCycleDescriptionId)
          .filter(e => e.ssoUserId !== this.approvers[index].ssoUserId)
          .map(e => e.approvalLevel)
          .filter(e => !isNaN(e) && e !== '')
          .map(e => Number(e))
          .sort());

        let skipped = 0;
        if (arr.length > 1) {
          skipped = arr.reduce((p, c, i, a) => c === i + a[0] ? p : i + a[0]++, void 0);
        }

        if (skipped) {
          this.addApproversForm.get('approvalLevel' + (index + 1)).markAsTouched();
          this.addApproversForm.get('approvalLevel' + (index + 1)).setErrors({invalidLevel: true});
        } else {
          if (this.addApproversForm.get('approvalLevel' + (index + 1)).errors
            && !this.addApproversForm.get('approvalLevel' + (index + 1)).errors.duplicateApprovalLevel
            && !this.addApproversForm.get('approvalLevel' + (index + 1)).errors.levelSkipped) {
            this.addApproversForm.get('approvalLevel' + (index + 1)).setErrors(null);
          }
        }
        setTimeout(() => {
          this.notifyParentApproversFormError(this.addApproversForm.invalid);
          this.pendingChangesGuard.isPristine = false;
        }, 400);
      }));
    });
    this.serviceArray = this.approvers;
    this.originalArray = this.approvers;
    this.dataSource = new MatTableDataSource(this.approvers);
    this.setDataSourceAttributes();
  }

  private _filterApprovalCycles(value: string, approver: any): any[] {
    if (!this.availableApprovalCycles) {
      return [];
    }
    value = value ? value : '';
    const prohibitedApprovalCycleDescriptionIds = this.approvers
      .filter(e => e.isParent)
      .filter(e => e.approvalCycleDescriptionId !== approver.approvalCycleDescriptionId)
      .map(e => e.approvalCycleDescriptionId);
    return this.availableApprovalCycles
      .filter(cycle => approver.approvalCycleDescriptionId === cycle.id || !prohibitedApprovalCycleDescriptionIds.includes(cycle.id))
      .filter(cycle => cycle.description.toLowerCase().includes(value.toString().toLowerCase())).sort((a, b) => {
        const x = (a.description ? a.description : '').toLowerCase().trim();
        const y = (b.description ? b.description : '').toLowerCase().trim();
        return x.localeCompare(y);
      });
  }

  private _filterApprovers(value: string): any[] {
    if (!this.availableApprovers) {
      return [];
    }
    value = value ? value : '';
    return this.availableApprovers
      .filter(approver => this.approvers.filter(e => e.ssoUserId === approver.ssoUserId
        && Number(e.approvalLevel) === Number(approver.approvalLevel)
        && e.approvalCycleDescriptionId === approver.approvalCycleDescriptionId).length === 0)
      .filter(approver => approver.name.toLowerCase().includes(value.toString().toLowerCase())).sort((a, b) => {
        const x = (a.name ? a.name : '').toLowerCase();
        const y = (b.name ? b.name : '').toLowerCase();
        return x.localeCompare(y);
      });
  }

}
